<template>
  <div class="evidence-diagnosis-view">
    <!-- 独立页面顶栏调试参数输入（仅 standalone 模式可见） -->
    <div v-if="standalone" class="standalone-bar pa-4 mb-4 rounded-xl bg-white card-shadow">
      <div class="d-flex align-center justify-space-between mb-3">
        <span class="font-weight-bold text-slate-800 text-subtitle-2">参数调试</span>
        <v-btn
          color="#2655f1"
          variant="flat"
          rounded="pill"
          size="small"
          class="text-white px-4"
          prepend-icon="mdi-refresh"
          :loading="loading"
          @click="startDiagnosis"
        >
          重新诊断
        </v-btn>
      </div>
      <v-row dense>
        <v-col cols="12" sm="3">
          <v-text-field v-model="manualForm.alarmCode" label="告警码" density="compact" variant="outlined" hide-details />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field v-model="manualForm.alarmTime" label="告警时刻" density="compact" variant="outlined" hide-details />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field v-model="manualForm.equipmentUuid" label="设备UUID" density="compact" variant="outlined"
            hide-details />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field v-model="manualForm.stationId" label="电站ID" density="compact" variant="outlined" hide-details />
        </v-col>
      </v-row>
    </div>

    <!-- 诊断过程阶段（对齐单机离线分析的标准规范：呼吸灯、步骤进度与超级表取证支撑） -->
    <div v-if="isProcessPhase" class="process-phase-wrapper mb-4">
      <EvidenceProcessTimeline :steps="processSteps" :sources="processSources" :phase="currentPhase" />
    </div>

    <!-- 错误异常提示 -->
    <div v-else-if="errorMessage && !evidenceData" class="error-box pa-8 text-center bg-white rounded-xl card-shadow">
      <v-icon size="36" color="#dc2626" class="mb-2">mdi-alert-circle-outline</v-icon>
      <div class="font-weight-bold text-slate-800 mb-1">诊断服务异常</div>
      <div class="text-caption text-slate-500 mb-3">{{ errorMessage }}</div>
      <el-button size="small" type="primary" @click="startDiagnosis">重试</el-button>
    </div>

    <!-- 未做取证分支 (unsupported / unresolved) -->
    <div v-else-if="evidenceData && !evidenceData.evaluated"
      class="unsupported-box pa-8 text-center bg-white rounded-xl card-shadow">
      <v-icon size="40" color="#64748b" class="mb-2">mdi-information-outline</v-icon>
      <div class="font-weight-bold text-slate-800 text-subtitle-1 mb-2">{{ displayVerdictLabel }}</div>
      <div class="text-body-2 text-slate-600 max-w-lg mx-auto mb-3">
        {{ evidenceData.reason || '本期未配置该告警码的证据链规则，未执行取证。' }}
      </div>
      <div class="text-caption text-slate-400">
        告警码：{{ alarmCode }} | 设备：{{ equipmentUuid }} | 电站：{{ stationId }}
      </div>
    </div>

    <!-- 核心诊断报告主体（完全无黑边框、像素级对齐、左右均衡呼吸感布局） -->
    <div v-else-if="evidenceData" class="report-container">
      <!-- 模块 1：告警主体与研判核心看板 (四层舒展大方排布，重点突出，彻底告别局促与挤压) -->
      <div class="summary-hero-card mb-4 pa-5 bg-white rounded-xl card-shadow">
        <!-- 第 1 层：告警身份主标题 (左) + 法定研判结论大标签 (右) -->
        <div class="d-flex align-center justify-space-between flex-wrap ga-3 mb-3">
          <!-- 左侧：告警大标题与代码级别 -->
          <div class="d-flex align-center flex-wrap">
            <h2 class="alarm-main-title text-slate-900 mr-3 mb-0">
              {{ displayAlarmName }}
            </h2>
            <span class="code-badge mr-2">{{ alarmCode }}</span>
            <span v-if="alarmCategory" class="category-pill mr-2" :class="getCategoryClass(alarmCategory)">
              <v-icon size="13" class="mr-1">{{ getCategoryIcon(alarmCategory) }}</v-icon>
              <span>{{ getCategoryLabel(alarmCategory) }}</span>
            </span>
          </div>

          <!-- 右侧：法定研判结论与置信度 (单行舒展不折行，极具视觉定性分量) -->
          <div class="d-flex align-center flex-wrap ga-2">
            <!-- <span class="verdict-banner-pill" :class="heroTagClass">
              <v-icon size="16" class="icon-inline">{{ heroIcon }}</v-icon>
              {{ displayVerdictLabel }}
            </span> -->
            <span v-if="confidenceText" class="badge-sub">
              置信度: {{ confidenceText }}
            </span>
            <span v-if="formattedElapsed" class="text-caption text-slate-400 ml-1">
              耗时 {{ formattedElapsed }}
            </span>
          </div>
        </div>

        <!-- 第 2 层：实体三要素 (所属电站、设备UUID、发生时刻) -->
        <div class="alarm-meta-strip py-2-5 px-3-5 rounded-lg bg-slate-50 mb-3 text-caption">
          <div class="prop-item mr-8 d-inline-flex align-center">
            <v-icon size="14" color="#64748b" class="icon-inline mr-1">mdi-office-building-outline</v-icon>
            <span class="text-slate-400 mr-1">所属电站:</span>
            <span class="text-slate-800 font-weight-medium">{{ stationName || '—' }}</span>
            <span v-if="stationId" class="text-slate-400 ml-1">({{ stationId }})</span>
          </div>
          <div class="prop-item mr-8 d-inline-flex align-center">
            <v-icon size="14" color="#64748b" class="icon-inline mr-1">mdi-developer-board</v-icon>
            <span class="text-slate-400 mr-1">设备UUID:</span>
            <span class="text-slate-800 font-weight-medium">{{ equipmentUuid }}</span>
          </div>
          <div class="prop-item d-inline-flex align-center">
            <v-icon size="14" color="#64748b" class="icon-inline mr-1">mdi-clock-outline</v-icon>
            <span class="text-slate-400 mr-1">告警发生时刻:</span>
            <span class="text-slate-800 font-weight-medium">{{ formattedAlarmTime }}</span>
          </div>
        </div>

        <!-- 第 3 层：综合研判分析结论 (重点突出横幅) -->
        <div class="verdict-highlight-banner pa-3 rounded-lg">
          <div class="d-flex align-start text-body-2">
            <span class="verdict-banner-badge mr-2 flex-shrink-0">
              <v-icon size="14" color="#1d4ed8" class="mr-1">mdi-text-box-search-outline</v-icon>
              研判结论
            </span>
            <div class="verdict-banner-text flex-grow-1 text-slate-800">
              <span class="font-weight-medium">{{ displayImpactText }}</span>
              <!-- <span v-if="analysisStatus === 'partial'" class="badge-sub badge-template ml-2" :title="reasonCodeText">
                确定性模板生成
              </span> -->
            </div>
          </div>
        </div>
      </div>

      <!-- 模块 2：核心触发判据事实核验大卡片 (同其他大卡片结构一致，内嵌三个小卡片) -->
      <div class="action-card bg-white rounded-xl pa-5 card-shadow mb-4">
        <!-- 卡片头部：标题、复现指标统计与右侧口径说明/测点提示 -->
        <div class="d-flex align-center justify-space-between mb-4 pb-2 border-b-subtle flex-wrap ga-2">
          <div class="d-flex align-center flex-wrap ga-2">
            <v-icon color="#2563eb" size="18" class="icon-inline">mdi-clipboard-text-search-outline</v-icon>
            <h4 class="text-subtitle-2 font-weight-bold text-slate-800 mb-0 mr-1">
              核心触发判据事实核验
            </h4>
            <!-- 触发条件复现指标 Badge：直接放在判据核验大卡片标题旁 -->
            <span class="kpi-stat-badge px-2 py-0-5 rounded d-inline-flex align-center text-caption"
              :class="metCount > 0 ? 'badge-met-count' : 'badge-zero-count'">
              <span class="text-slate-500 mr-1">触发条件已复现:</span>
              <strong class="font-weight-bold mr-1" :class="metCount > 0 ? 'text-red-600' : 'text-slate-900'">
                {{ metCount }}
              </strong>
              <span class="text-slate-400 mr-1">/</span>
              <span class="text-slate-600 font-weight-medium">{{ needCount }} 项</span>
            </span>
          </div>

          <!-- 右侧：缺失测点警告 + 口径折叠 -->
          <div class="d-flex align-center ga-3 flex-wrap">
            <span v-if="unavailablePoints.length"
              class="missing-probe-chip text-caption text-amber-800 d-inline-flex align-center px-2 py-0-5 rounded">
              <v-icon size="14" color="#d97706" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>探针未采集测点: {{ unavailablePointsText }}</span>
            </span>
            <button v-if="ruleStatement" type="button" class="link-btn text-primary d-inline-flex align-center"
              @click="showRuleDetail = !showRuleDetail">
              <span>{{ showRuleDetail ? '收起口径说明' : '查看判定与换算口径' }}</span>
              <v-icon size="14" class="ml-1">{{ showRuleDetail ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </button>
          </div>
        </div>

        <!-- 可折叠口径细节 -->
        <div v-if="showRuleDetail"
          class="rule-detail-expand pa-3 mb-4 rounded-lg bg-slate-50 text-caption text-slate-600">
          <div v-if="ruleStatement" class="mb-1">
            <strong class="text-slate-700">判定口径：</strong>{{ ruleStatement }}
          </div>
          <div v-for="(ts, idx) in transformStatements" :key="idx" class="mt-1">
            <strong class="text-slate-700">测点换算：</strong>{{ ts.text }}
          </div>
        </div>

        <!-- 卡片内部：三栏小卡片网格 -->
        <div class="condition-cards-grid">
          <div v-for="cond in conditions" :key="cond.conditionId"
            class="cond-card pa-4 rounded-xl d-flex flex-column justify-space-between">
            <!-- 卡片头部：测点名与判定状态 -->
            <div>
              <div class="d-flex align-start justify-space-between mb-2">
                <div>
                  <div class="font-weight-bold text-slate-900 text-body-2">
                    {{ cond.label || cond.point }}
                  </div>
                  <!-- <div class="text-caption text-slate-400">
                    {{ cond.point }}
                  </div> -->
                </div>
                <span class="cond-badge" :class="getOutcomeBadgeClass(cond.outcome)">
                  {{ getOutcomeText(cond.outcome, cond.unknownKind) }}
                </span>
              </div>

              <!-- 智能体事实解读简评 (纯文本段落，去除突兀白底框) -->
              <div v-if="getNarrative(cond.conditionId)" class="cond-narrative mb-3 text-slate-600">
                {{ getNarrative(cond.conditionId) }}
              </div>
            </div>

            <!-- 卡片中间：窗口极值与阈值大字比对 -->
            <div class="cond-values-row d-flex align-baseline justify-space-between mb-2">
              <div class="val-block">
                <span class="text-caption text-slate-400 d-block mb-0-5">窗口极值</span>
                <div class="d-flex align-baseline ga-1">
                  <span class="val-num font-weight-bold"
                    :class="cond.outcome === 'met' ? 'text-red-600' : 'text-slate-900'">
                    {{ formatVal(cond.comparedExtreme) }}
                  </span>
                  <span class="val-unit text-caption text-slate-500 font-weight-medium">
                    {{ cond.unit || '' }}
                  </span>
                </div>
              </div>

              <div class="thresh-block text-right">
                <span class="text-caption text-slate-400 d-inline-flex align-center justify-end mb-0-5">
                  <span>触发阈值</span>
                  <el-tooltip v-if="cond.transform" content="该指标在告警判定规则中需先将物理 NTC 采样温度换算为软件判据值（Soft），再与 100℃ 阈值比对"
                    placement="top">
                    <v-icon size="13" color="#94a3b8" class="ml-1 cursor-pointer">mdi-help-circle-outline</v-icon>
                  </el-tooltip>
                </span>
                <div class="thresh-num font-weight-bold text-slate-700">
                  {{ cond.op }} {{ cond.threshold }} {{ cond.unit || '' }}
                </div>
              </div>
            </div>

            <!-- 阈值占比进度条与波动区间 -->
            <div class="progress-wrap mb-3">
              <div class="d-flex justify-space-between text-caption mb-1">
                <span class="text-slate-400">
                  占阈值比例:
                  <strong :class="getPercentColor(cond.windowMaxPercent)">
                    {{ cond.windowMaxPercent != null ? `${cond.windowMaxPercent}%` : '—' }}
                  </strong>
                </span>
                <span v-if="getValueRange(cond.conditionId)" class="text-slate-500">
                  波动区间: {{ getValueRange(cond.conditionId) }} {{ cond.unit }}
                </span>
              </div>
              <div class="progress-track">
                <div class="progress-fill" :class="getPercentFillClass(cond.windowMaxPercent)"
                  :style="{ width: getProgressWidth(cond.windowMaxPercent) }" />
              </div>
            </div>

            <!-- 卡片底部辅助指标 -->
            <div
              class="cond-footer pt-2 border-t-subtle d-flex align-center justify-space-between text-caption text-slate-500">
              <span>瞬时采样: <strong class="text-slate-700">{{ formatVal(cond.alarmNearestCompared)
                  }}</strong></span>
              <span>超阈值点数: <strong :class="cond.hitCount > 0 ? 'text-red-600' : 'text-slate-700'">{{
                cond.hitCount != null ? cond.hitCount : '—' }}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <!-- 模块 3：时序证据曲线 (ECharts，内嵌多源超级表事实底座) -->
      <div class="section-block mb-4">
        <EvidenceTelemetryChart v-if="chartsData" :charts="chartsData" :sources="sourcesList"
          :queries-issued="queriesIssued" :elapsed-ms="elapsedMs" />
      </div>

      <!-- 模块 4：现场标准排查规程 (SOP，通栏整行，像素级基准线对齐) -->
      <div class="action-card bg-white rounded-xl pa-5 card-shadow mb-4">
        <div class="d-flex align-center justify-space-between mb-3 pb-2 border-b-subtle">
          <div class="d-flex align-center">
            <v-icon color="#ea580c" size="20" class="icon-inline">mdi-clipboard-check-outline</v-icon>
            <h4 class="text-subtitle-2 font-weight-bold text-slate-800 mb-0">现场标准排查规程 (SOP)</h4>
          </div>
          <span class="text-caption text-red-600 font-weight-bold">
            复核完成前严格禁止远程解除
          </span>
        </div>

        <div class="aligned-flow-list">
          <div v-for="(step, sIdx) in opsRecommendations" :key="sIdx"
            class="flow-item d-flex align-start ga-2 mb-2 text-body-2 text-slate-800">
            <span class="step-badge mr-2">{{ sIdx + 1 }}</span>
            <span class="step-text">{{ cleanStepText(step) }}</span>
          </div>
        </div>

        <div class="text-caption text-slate-400 pt-2 border-t-subtle mt-3">
          注：首项为规则既定严重故障处置 SOP 基线，后接本次诊断针对性补充建议。
        </div>
      </div>

      <!-- 模块 5：研发侧排查建议与客观安全边界 (单列整齐流式布局，严格统一正文字号与层级) -->
      <div v-if="hasDevCardContent" class="action-card bg-white rounded-xl pa-5 card-shadow mb-4">
        <!-- 5.1 研发侧排查建议 -->
        <div v-if="devRecommendations.length" :class="{ 'mb-4': hypotheses.length || limitationsList.length }">
          <div class="d-flex align-center mb-3 pb-2 border-b-subtle">
            <v-icon color="#4f46e5" size="18" class="icon-inline mr-1">mdi-code-braces</v-icon>
            <h4 class="text-subtitle-2 font-weight-bold text-slate-800 mb-0">研发侧排查建议</h4>
          </div>
          <div class="aligned-flow-list">
            <div v-for="(step, dIdx) in devRecommendations" :key="dIdx"
              class="flow-item d-flex align-start mb-2 text-body-2 text-slate-700">
              <span class="dot-prefix text-indigo-600 mr-2">•</span>
              <span class="step-text">{{ cleanStepText(step) }}</span>
            </div>
          </div>
        </div>

        <!-- 5.2 待验证原因假设 (同级标题与正文字号) -->
        <div v-if="hypotheses.length" class="pt-3 border-t-subtle" :class="{ 'mb-4': limitationsList.length }">
          <div class="d-flex align-center mb-2">
            <v-icon size="18" color="#2563eb" class="icon-inline mr-1">mdi-help-circle-outline</v-icon>
            <h4 class="text-subtitle-2 font-weight-bold text-slate-800 mb-0">待验证原因假设</h4>
          </div>
          <div class="aligned-flow-list">
            <div v-for="(h, hIdx) in hypotheses" :key="hIdx"
              class="flow-item d-flex align-start mb-2 text-body-2 text-slate-700">
              <span class="dot-prefix text-blue-600 mr-2">•</span>
              <span class="step-text">{{ cleanStepText(h) }}</span>
            </div>
          </div>
        </div>

        <!-- 5.3 客观取证边界声明 (云端历史数据未覆盖项，同级标题与正文字号) -->
        <div v-if="limitationsList.length" class="pt-3 border-t-subtle">
          <div class="d-flex align-center mb-2">
            <v-icon size="18" color="#d97706" class="icon-inline mr-1">mdi-shield-alert-outline</v-icon>
            <h4 class="text-subtitle-2 font-weight-bold text-amber-900 mb-0">客观取证边界声明（云端历史数据未覆盖项）</h4>
          </div>
          <div class="aligned-flow-list">
            <div v-for="(item, idx) in limitationsList" :key="idx"
              class="flow-item d-flex align-start mb-2 text-body-2 text-slate-700">
              <span class="dot-prefix text-amber-600 mr-2">•</span>
              <span class="step-text">{{ cleanStepText(item) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { buildEvidenceDiagnosisPayload, streamEvidenceDiagnosis } from '@/api/evidenceDiagnosis'
import { getCategoryClass, getCategoryIcon, getCategoryLabel } from '@/views/workbench/components/Tasks/AlarmReportDrawer/category'
import EvidenceTelemetryChart from './EvidenceTelemetryChart.vue'
import EvidenceProcessTimeline from './EvidenceProcessTimeline.vue'

const props = defineProps({
  alarmRecord: { type: Object, default: null },
  standalone: { type: Boolean, default: false },
})

// 分析流程 4 个核心步骤
const DEFAULT_STEPS = [
  { id: 'step-probe', text: '确认告警设备与关联测点', status: 'pending', statusDisplay: '' },
  { id: 'step-collect', text: '调取告警前后运行数据', status: 'pending', statusDisplay: '' },
  { id: 'step-verify', text: '核对告警触发条件', status: 'pending', statusDisplay: '' },
  { id: 'step-llm', text: 'AI 分析原因并给出处理建议', status: 'pending', statusDisplay: '' },
]

// 调取的 4 项关键运行数据
const DEFAULT_SOURCES = [
  {
    id: 'fire_sys_pack',
    stable: 'fire_sys_pack',
    label: 'PACK 消防数据',
    description: 'PACK 内 CO 浓度、环境温度等',
    searchingDescription: '正在查询 CO 浓度与环境温度...',
    status: 'pending',
    rowCount: null,
    statusDisplay: '等待查询',
    note: '',
  },
  {
    id: 'eblock_info',
    stable: 'eblock_info',
    label: '电池运行数据',
    description: '电芯最高温、运行状态、实际 SOC 等',
    searchingDescription: '正在查询电芯最高温与 SOC 运行数据...',
    status: 'pending',
    rowCount: null,
    statusDisplay: '等待查询',
    note: '',
  },
  {
    id: 'fire_sys_cabinet',
    stable: 'fire_sys_cabinet',
    label: '柜体消防数据',
    description: '柜内环境温度、喷淋状态等',
    searchingDescription: '正在查询柜内温度与喷淋状态...',
    status: 'pending',
    rowCount: null,
    statusDisplay: '等待查询',
    note: '',
  },
  {
    id: 'eblock_aircon',
    stable: 'eblock_aircon',
    label: '空调运行数据',
    description: '空调回风温度',
    searchingDescription: '正在查询空调回风温度...',
    status: 'pending',
    rowCount: null,
    statusDisplay: '等待查询',
    note: '',
  },
]

const loading = ref(false)
const isProcessPhase = ref(false)
const currentPhase = ref('connecting')
const processSteps = ref(JSON.parse(JSON.stringify(DEFAULT_STEPS)))
const processSources = ref(JSON.parse(JSON.stringify(DEFAULT_SOURCES)))

const errorMessage = ref('')
let abortController = null

const analysisId = ref('')
const analysisStatus = ref('idle')
const reasonCode = ref('')
const elapsedMs = ref(null)
const evidenceData = ref(null)
const diagnosisData = ref(null)
const chartsData = ref(null)
const analysisData = ref(null)
const contextData = ref(null)

const showRuleDetail = ref(false)

const manualForm = reactive({
  alarmCode: '',
  alarmTime: '',
  equipmentUuid: '',
  stationId: '',
  alarmName: '',
})

const effectiveRecord = computed(() => {
  if (props.alarmRecord) return props.alarmRecord
  return {
    alarmCode: manualForm.alarmCode,
    alarmTime: manualForm.alarmTime,
    lastTime: manualForm.alarmTime,
    uuid: manualForm.equipmentUuid,
    stationId: manualForm.stationId,
    description: manualForm.alarmName || '电池热失控',
  }
})

const alarmCode = computed(() => {
  return evidenceData.value?.alarmCode || effectiveRecord.value?.alarmCode || effectiveRecord.value?.code || '—'
})

const displayAlarmName = computed(() => {
  return evidenceData.value?.ruleName || effectiveRecord.value?.description || effectiveRecord.value?.alarmName || '电池热失控'
})

const stationName = computed(() => effectiveRecord.value?.stationName || '')
const stationId = computed(() => evidenceData.value?.stationId || effectiveRecord.value?.stationId || '')
const equipmentUuid = computed(() => evidenceData.value?.equipmentUuid || effectiveRecord.value?.uuid || effectiveRecord.value?.deviceVid || '')
const alarmTime = computed(() => evidenceData.value?.alarmTime || effectiveRecord.value?.lastTime || effectiveRecord.value?.startTime || '')

const alarmRecordId = computed(() => contextData.value?.alarmId || effectiveRecord.value?.id || '')
const severityLevel = computed(() => contextData.value?.severityLevel || effectiveRecord.value?.level || '')

const alarmCategory = computed(() => {
  const direct = effectiveRecord.value?.category || contextData.value?.category || effectiveRecord.value?.alarmCategory
  if (direct) {
    return String(direct).trim().toUpperCase()
  }
  const code = String(alarmCode.value || '').trim()
  const name = String(displayAlarmName.value || '').trim()
  const lvl = String(severityLevel.value || '').trim()

  if (code.startsWith('A1_02_') || name.includes('热失控') || lvl === '3' || lvl === '严重') {
    return 'SAFETY'
  }
  if (lvl === '2' || lvl === '故障') {
    return 'INCOME'
  }
  return 'NORMAL'
})

const windowText = computed(() => {
  const w = evidenceData.value?.window
  if (!w || !w.start || !w.end) return '告警前后 30 分钟'
  const s = String(w.start).replace('T', ' ').slice(11, 19)
  const e = String(w.end).replace('T', ' ').slice(11, 19)
  return `${s} ~ ${e}`
})

const metCount = computed(() => evidenceData.value?.met ?? 0)
const needCount = computed(() => evidenceData.value?.need ?? 2)
const unavailablePoints = computed(() => evidenceData.value?.unavailablePoints || [])

const POINT_NAMES = {
  aircon_cabinet_in_temp: '空调回风温度',
  pack_co_concentration: 'PACK 内 CO 浓度',
  pack_ambient_temp: 'PACK 环境温度',
  cell_temp_max: '单体电芯最高温度',
  cabinet_ambient_temp: '柜体消防温度',
  cabinet_spraying: '柜体喷洒状态',
}

const unavailablePointsText = computed(() => {
  return unavailablePoints.value.map((p) => POINT_NAMES[p] || p).join('、')
})
const queriesIssued = computed(() => evidenceData.value?.queriesIssued ?? 4)

const conditions = computed(() => evidenceData.value?.conditions || [])
const ruleStatement = computed(() => analysisData.value?.ruleStatement || '')
const transformStatements = computed(() => analysisData.value?.transformStatements || [])
const sourcesList = computed(() => analysisData.value?.sources || evidenceData.value?.sources || [])
const limitationsList = computed(() => analysisData.value?.coverageLimitations || [])
const hypotheses = computed(() => diagnosisData.value?.pendingHypotheses || [])
const opsRecommendations = computed(() => diagnosisData.value?.opsRecommendations || [])
const devRecommendations = computed(() => diagnosisData.value?.devRecommendations || [])

const hasDevCardContent = computed(() => devRecommendations.value.length > 0 || hypotheses.value.length > 0 || limitationsList.value.length > 0)

const confidenceText = computed(() => {
  const c = diagnosisData.value?.confidence
  if (c === 'high') return '高'
  if (c === 'medium') return '中'
  if (c === 'low') return '低'
  return ''
})

const reasonCodeText = computed(() => {
  if (reasonCode.value === 'OUTPUT_REJECTED') return '模型输出未通过确定性规则核验，安全回退至法定确定性模板'
  if (reasonCode.value === 'LLM_TIMEOUT') return '推理超时，安全回退至确定性模板'
  return reasonCode.value || ''
})

const displayVerdictLabel = computed(() => {
  if (evidenceData.value?.verdictLabel) return evidenceData.value.verdictLabel
  const v = evidenceData.value?.verdict || diagnosisData.value?.verdict
  if (v === 'not_reproduced') return '触发条件未在云端历史数据中复现（不等于误报）'
  if (v === 'triggered') return '触发条件已在云端数据中确证复现'
  if (v === 'inconclusive') return '证据不足，无法判断触发条件是否复现'
  if (v === 'unsupported') return '本期未配置该告警码的证据链规则，未做取证'
  if (v === 'unresolved') return '设备坐标未能解析，未做取证'
  return '诊断完成'
})

const displayVerdictTitle = computed(() => displayVerdictLabel.value)

const formattedAlarmTime = computed(() => {
  if (!alarmTime.value) return '—'
  return String(alarmTime.value).replace('T', ' ')
})

const formattedElapsed = computed(() => {
  if (elapsedMs.value == null) return ''
  if (elapsedMs.value >= 1000) {
    return `${(elapsedMs.value / 1000).toFixed(1)}s`
  }
  return `${elapsedMs.value}ms`
})

const verdictBoxClass = computed(() => {
  const v = evidenceData.value?.verdict || diagnosisData.value?.verdict
  if (v === 'not_reproduced') return 'box-not-reproduced'
  if (v === 'triggered') return 'box-triggered'
  if (v === 'inconclusive') return 'box-inconclusive'
  return 'box-default'
})

const verdictBoxTextColor = computed(() => {
  const v = evidenceData.value?.verdict || diagnosisData.value?.verdict
  if (v === 'not_reproduced') return 'text-indigo-900'
  if (v === 'triggered') return 'text-red-900'
  if (v === 'inconclusive') return 'text-amber-900'
  return 'text-slate-900'
})

const verdictBoxIconColor = computed(() => {
  const v = evidenceData.value?.verdict || diagnosisData.value?.verdict
  if (v === 'not_reproduced') return '#4338ca'
  if (v === 'triggered') return '#dc2626'
  if (v === 'inconclusive') return '#d97706'
  return '#2563eb'
})

const displayImpactText = computed(() => {
  return diagnosisData.value?.impactNarrative || evidenceData.value?.reason || '本次结论不足以解除告警，处置仍按有风险对待，需人工复核。'
})

// 波动区间映射
const valueRangeMap = computed(() => {
  const map = {}
  const list = analysisData.value?.conditions || []
  list.forEach(item => {
    if (item.conditionId && item.valueRange) {
      const vr = item.valueRange
      const min = vr.comparedMin != null ? vr.comparedMin : vr.rawMin
      const max = vr.comparedMax != null ? vr.comparedMax : vr.rawMax
      if (min != null && max != null) {
        map[item.conditionId] = `${min} ~ ${max}`
      }
    }
  })
  return map
})

function getValueRange(id) {
  return valueRangeMap.value[id] || null
}

// 事实解读映射
const narrativeMap = computed(() => {
  const map = {}
  const list = diagnosisData.value?.conditionNarratives || []
  list.forEach(item => {
    if (item.conditionId) map[item.conditionId] = item.narrative
  })
  return map
})

function getNarrative(id) {
  return narrativeMap.value[id] || ''
}

const heroTagClass = computed(() => {
  const v = evidenceData.value?.verdict
  if (v === 'triggered') return 'tag-triggered'
  if (v === 'not_reproduced') return 'tag-not-reproduced'
  if (v === 'inconclusive') return 'tag-inconclusive'
  return 'tag-default'
})

const heroIcon = computed(() => {
  const v = evidenceData.value?.verdict
  if (v === 'triggered') return 'mdi-alert-octagon'
  if (v === 'not_reproduced') return 'mdi-shield-alert-outline'
  if (v === 'inconclusive') return 'mdi-help-circle-outline'
  return 'mdi-information-outline'
})

function formatVal(val) {
  if (val == null || val === '') return '—'
  const num = Number(val)
  return Number.isNaN(num) ? '—' : Number(num.toFixed(1))
}

function getOutcomeText(outcome, unknownKind) {
  if (outcome === 'met') return '已超阈值'
  if (outcome === 'not_met') return '未超阈值'
  if (outcome === 'unknown') {
    if (unknownKind === 'no_data') return '未能取证(无采样)'
    if (unknownKind === 'truncated') return '未能取证(触顶)'
    return '未能取证'
  }
  return outcome || '—'
}

function getOutcomeBadgeClass(outcome) {
  if (outcome === 'met') return 'badge-met'
  if (outcome === 'not_met') return 'badge-not-met'
  return 'badge-unknown'
}

function getPercentColor(percent) {
  if (percent == null) return 'text-slate-400'
  if (percent >= 100) return 'text-red-600'
  if (percent >= 60) return 'text-amber-600'
  return 'text-blue-600'
}

function getPercentFillClass(percent) {
  if (percent == null) return 'fill-gray'
  if (percent >= 100) return 'fill-red'
  if (percent >= 60) return 'fill-amber'
  return 'fill-blue'
}

function getProgressWidth(percent) {
  if (percent == null) return '0%'
  const clamped = Math.max(0, Math.min(100, Number(percent)))
  return `${clamped}%`
}

function cleanStepText(text) {
  if (!text) return ''
  return String(text).replace(/^(\d+[\.\、\s]+)+/, '')
}

function syncManualFormFromRecord(rec) {
  if (!rec) return
  manualForm.alarmCode = rec.alarmCode || rec.code || 'A1_02_00_1012'
  manualForm.alarmTime = rec.lastTime && rec.lastTime !== '--' ? rec.lastTime : (rec.startTime || '2026-09-08 11:23:47')
  manualForm.equipmentUuid = rec.uuid && rec.uuid !== '--' ? rec.uuid : (rec.deviceVid || 'S1164_ELK1_EB1')
  manualForm.stationId = String(rec.stationId || '1164')
  manualForm.alarmName = rec.description || rec.alarmName || '电池热失控'
}



async function startDiagnosis() {
  if (abortController) abortController.abort()
  abortController = new AbortController()

  errorMessage.value = ''
  evidenceData.value = null
  diagnosisData.value = null
  chartsData.value = null
  analysisData.value = null
  reasonCode.value = ''
  loading.value = true
  isProcessPhase.value = true
  currentPhase.value = 'connecting'

  // 重置步骤与数据源
  processSteps.value = JSON.parse(JSON.stringify(DEFAULT_STEPS))
  processSources.value = JSON.parse(JSON.stringify(DEFAULT_SOURCES))

  // 步骤 1 启动：确认告警设备与关联测点
  processSteps.value[0].status = 'started'
  processSteps.value[0].statusDisplay = '确认中...'

  let payload = null
  try {
    const targetSource = props.alarmRecord || manualForm
    payload = buildEvidenceDiagnosisPayload(targetSource)
  } catch (err) {
    loading.value = false
    isProcessPhase.value = false
    errorMessage.value = err.message || '入参组装失败'
    ElMessage.error(errorMessage.value)
    return
  }

  try {
    await streamEvidenceDiagnosis(payload, {
      // 1. 后端正式启动事件 (analysis_started)
      onStarted(evt) {
        if (evt.analysisId) analysisId.value = evt.analysisId

        // 步骤 1 真实完成
        processSteps.value[0].status = 'completed'
        processSteps.value[0].statusDisplay = '已完成（锁定设备及 4 项相关测点）'

        // 步骤 2 真实启动：调取告警前后运行数据
        currentPhase.value = 'collecting'
        processSteps.value[1].status = 'started'
        processSteps.value[1].statusDisplay = '查询中（正在拉取历史运行数据）...'

        // 4 项数据卡片全部进入真实查询态
        processSources.value.forEach((s) => {
          s.status = 'started'
          s.statusDisplay = '查询中...'
        })
      },

      // 2. 步骤阶段进度事件 (step_detail)
      onStepDetail(evt) {
        if (evt.step_id === 'generate_diagnosis' && evt.status === 'started') {
          // 步骤 4 真实启动：大模型开始生成处理建议
          currentPhase.value = 'generating'
          processSteps.value[3].status = 'started'
          processSteps.value[3].statusDisplay = '分析中（正在生成现场处置建议）...'
        }
      },

      // 3. 证据数据就绪事件 (evidence_ready)
      onEvidenceReady(evt) {
        if (evt.evidence) {
          evidenceData.value = evt.evidence

          // 步骤 2 真实完成
          processSteps.value[1].status = 'completed'
          processSteps.value[1].statusDisplay = '已完成（4 项运行数据已获取）'

          // 4 项运行数据按真实数据源点亮
          if (Array.isArray(evt.evidence.sources)) {
            evt.evidence.sources.forEach((src) => {
              const target = processSources.value.find(
                (s) => s.stable === src.stable || s.id === src.stable || (src.label && s.label.includes(src.label))
              )
              if (target) {
                target.rowCount = src.rowCount
                if (src.status === 'ok') {
                  target.status = 'completed'
                  target.statusDisplay = `已获取 (${src.rowCount} 条)`
                } else if (src.status === 'no_device_series') {
                  target.status = 'completed'
                  target.statusDisplay = '暂无数据'
                  target.note = '该设备未采集此项数据'
                } else {
                  target.status = 'failed'
                  target.statusDisplay = '获取失败'
                }
              }
            })
          }

          // 步骤 3 真实完成：此时规则判据比对已经由后端计算完成
          currentPhase.value = 'verifying'
          const condLen = evt.evidence.conditions?.length || 3
          processSteps.value[2].status = 'completed'
          processSteps.value[2].statusDisplay = `已完成（${condLen} 项指标均未超标，历史数据中未复现）`
        }
      },

      // 4. 诊断完成事件 (analysis_completed)
      onCompleted(evt) {
        analysisStatus.value = evt.status || 'success'
        reasonCode.value = evt.reasonCode || ''
        if (evt.elapsedMs != null) elapsedMs.value = evt.elapsedMs
        if (evt.evidence) evidenceData.value = evt.evidence
        if (evt.diagnosis) diagnosisData.value = evt.diagnosis
        if (evt.charts) chartsData.value = evt.charts
        if (evt.analysis) analysisData.value = evt.analysis
        if (evt.context) contextData.value = evt.context

        // 步骤 4 真实完成
        currentPhase.value = 'success'
        processSteps.value[3].status = 'completed'
        processSteps.value[3].statusDisplay = '已完成（处理建议已生成）'

        // 短暂停顿 600ms 让用户看清全绿就绪看板后，平滑展开报告
        setTimeout(() => {
          isProcessPhase.value = false
          loading.value = false
        }, 600)
      },

      // 5. 失败中断事件 (onFailed)
      onFailed(evt) {
        currentPhase.value = 'failed'
        errorMessage.value = evt.message || '诊断服务异常中断'
        isProcessPhase.value = false
        loading.value = false
      },

      onDone(evt) {
        if (evt.total_elapsed_ms != null) elapsedMs.value = evt.total_elapsed_ms
      },
    }, abortController.signal)
  } catch (error) {
    if (error.name !== 'AbortError') {
      currentPhase.value = 'failed'
      errorMessage.value = error.message || '网络请求异常'
      isProcessPhase.value = false
      loading.value = false
    }
  }
}


watch(() => props.alarmRecord, (newVal) => {
  if (newVal) {
    syncManualFormFromRecord(newVal)
    startDiagnosis()
  }
}, { immediate: true, deep: true })

onMounted(() => {
  if (props.standalone && !props.alarmRecord) {
    syncManualFormFromRecord(null)
  }
})

onBeforeUnmount(() => {
  if (abortController) {
    abortController.abort()
    abortController = null
  }
})

const emit = defineEmits(['loading-change'])

watch(loading, (val) => {
  emit('loading-change', val)
})

defineExpose({
  startDiagnosis,
})
</script>

<style scoped lang="scss">
.evidence-diagnosis-view {
  color: #1e293b;
}

/* 纯净微阴影代替生硬黑边框 */
.card-shadow {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.border-t-subtle {
  border-top: 1px solid #f8fafc;
}

.border-b-subtle {
  border-bottom: 1px solid #f8fafc;
}

/* 模块 1：双核心 Hero Banner */
.summary-hero-card {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.alarm-main-title {
  font-size: 20px;
  line-height: 1.25;
  letter-spacing: -0.01em;
}

.code-badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
  font-weight: 500;
}

.category-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;

  &.cat-safety {
    background: #fee2e2;
    color: #dc2626;
  }

  &.cat-income {
    background: #fef3c7;
    color: #d97706;
  }

  &.cat-normal {
    background: #eff6ff;
    color: #2563eb;
  }
}

.severity-pill {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #fee2e2;
  color: #dc2626;
  font-weight: 600;
}

.icon-inline {
  margin-right: 6px !important;
  vertical-align: text-bottom;
}

.alarm-meta-strip {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 8px;
}

.prop-item {
  display: inline-flex;
  align-items: center;
  line-height: 1.4;
  white-space: nowrap;

  .icon-inline {
    margin-right: 4px;
    flex-shrink: 0;
  }
}

.verdict-banner-pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 6px;
  white-space: nowrap;

  &.tag-not-reproduced {
    background: #eef2ff;
    color: #3730a3;
  }

  &.tag-triggered {
    background: #fef2f2;
    color: #b91c1c;
  }

  &.tag-inconclusive {
    background: #fffbeb;
    color: #b45309;
  }

  &.tag-default {
    background: #f1f5f9;
    color: #334155;
  }
}

.badge-sub {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
  font-weight: 500;

  &.badge-template {
    background: #fef3c7;
    color: #92400e;
  }
}

.kpi-stat-badge {
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  line-height: 1.4;

  &.badge-met-count {
    background-color: #fef2f2;
    border-color: #fecaca;
  }
}

/* 研判结论高亮横幅 (重点突出，专业工控风格) */
.verdict-highlight-banner {
  background: #f0f7ff;
  border: 1px solid #dbeafe;
  line-height: 1.6;
}

.verdict-banner-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 700;
  color: #1d4ed8;
  background: #dbeafe;
  padding: 2px 7px;
  border-radius: 4px;
  line-height: 1.35;
  margin-top: 1px;
}

.verdict-banner-text {
  font-size: 13.5px;
  line-height: 1.6;
}

.link-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  color: #2563eb;

  &:hover {
    text-decoration: underline;
  }
}

.missing-probe-chip {
  background-color: #fffbeb;
  border: 1px solid #fef3c7;
}

/* 判据卡片三栏网格 */
.condition-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.cond-card {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    border-color: #e2e8f0;
  }
}

.cond-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;

  &.badge-not-met {
    background: #eff6ff;
    color: #2563eb;
  }

  &.badge-met {
    background: #fef2f2;
    color: #dc2626;
  }

  &.badge-unknown {
    background: #f1f5f9;
    color: #64748b;
  }
}

.cond-narrative {
  background: transparent !important;
  border: none !important;
  font-size: 12px;
  line-height: 1.6;
}

.val-num {
  font-size: 26px;
  line-height: 1;
}

.thresh-num {
  font-size: 15px;
}

/* 进度条 */
.progress-track {
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;

  &.fill-blue {
    background: #2563eb;
  }

  &.fill-amber {
    background: #d97706;
  }

  &.fill-red {
    background: #dc2626;
  }

  &.fill-gray {
    background: #94a3b8;
  }
}

/* 统一无误差严格对齐的流式列表 (解决参差不齐的对齐痛点) */
.aligned-flow-list {
  display: flex;
  flex-direction: column;
}

.flow-item {
  line-height: 1.7;

  .step-badge {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #eff6ff;
    color: #2563eb;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 3px;
  }

  .dot-prefix {
    width: 14px;
    text-align: center;
    font-size: 16px;
    line-height: 1.4;
    flex-shrink: 0;
    color: #64748b;
  }

  .step-text {
    flex: 1;
    min-width: 0;
  }
}
</style>
